import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plug, Check, X, RefreshCw, ExternalLink, MessageSquare, Loader2, AlertTriangle } from "lucide-react";

// Bridge state → badge presentation. `bridge_state` is computed server-side
// from status + expires_at + provider config so the UI never has to guess.
const BRIDGE_BADGE = {
  active: { label: "Active", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  token_expired: { label: "Token expired", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  token_missing: { label: "Token invalid", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  token_invalid: { label: "Token invalid", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  reauth_needed: { label: "Reauth needed", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  provider_id_missing: { label: "Provider not configured", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  not_installed: { label: "Not installed", cls: "bg-zinc-800/40 text-zinc-400 border-zinc-700" },
};

// Admin-only panel that lives inside the existing user detail view. It
// manages the ONE GoHighLevel sub-account connected to `userId`. The PIT
// is never displayed after saving — only the masked form.
export default function GhlConnectionPanel({ userId }) {
  const [state, setState] = useState({ status: "loading" });
  const [locId, setLocId] = useState("");
  const [pit, setPit] = useState("");
  const [busy, setBusy] = useState(false);
  const [bridge, setBridge] = useState(null);
  const [bridgeBusy, setBridgeBusy] = useState(false);
  const [lastAttempt, setLastAttempt] = useState(null);

  const load = () => {
    if (!userId) return;
    setState({ status: "loading" });
    api
      .get(`/admin/employz/connection/${userId}`)
      .then((r) => setState(r.data || { status: "not_connected" }))
      .catch(() => setState({ status: "not_connected" }));
    api
      .get(`/admin/employz-app/install-status/${userId}`)
      .then((r) => setBridge(r.data || { status: "not_installed", bridge_state: "not_installed" }))
      .catch(() => setBridge({ status: "not_installed", bridge_state: "not_installed" }));
    api
      .get(`/admin/employz-app/last-install-attempt/${userId}`)
      .then((r) => setLastAttempt(r.data?.found ? r.data : null))
      .catch(() => setLastAttempt(null));
  };

  useEffect(() => {
    setLocId("");
    setPit("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const connect = async () => {
    if (!locId.trim() || !pit.trim()) {
      toast.error("Location ID and Private Integration Token are both required.");
      return;
    }
    setBusy(true);
    try {
      const resp = await api.post("/admin/employz/connect", {
        user_id: userId,
        location_id: locId.trim(),
        pit: pit.trim(),
      });
      setState({ ...resp.data, status: "connected" });
      setLocId("");
      setPit("");
      toast.success("CRM connected — score fields ready in this sub-account.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect to that CRM sub-account.");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/employz/test/${userId}`);
      toast.success("Connection verified.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test call failed.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Disconnect this member's CRM sub-account? Nothing inside the CRM is deleted.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/employz/connection/${userId}`);
      toast.success("Disconnected.");
      setState({ status: "not_connected" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed.");
    } finally {
      setBusy(false);
    }
  };

  const startBridgeInstall = async () => {
    setBridgeBusy(true);
    try {
      const r = await api.get("/employz-app/install-url");
      // Copy the URL so the admin can send it to the target user, since OAuth
      // requires the target sub-account owner's login. Also open a new tab in
      // case the admin IS also that user.
      try { await navigator.clipboard.writeText(r.data.url); } catch (_e) { /* ignore */ }
      window.open(r.data.url, "_blank", "noopener");
      toast.success("Install URL copied to clipboard. Send it to the user or complete the OAuth flow yourself.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build install URL — check GHL_APP_CLIENT_ID env var.");
    } finally { setBridgeBusy(false); }
  };

  const disconnectBridge = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Disconnect the Employz WhatsApp Bridge for this user? Inbound WhatsApp will revert to note+task mirror.")) return;
    setBridgeBusy(true);
    try {
      await api.delete(`/admin/employz-app/install/${userId}`);
      toast.success("Bridge disconnected — will revert to note+task on next inbound.");
      setBridge({ status: "not_installed" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed.");
    } finally { setBridgeBusy(false); }
  };

  return (
    <div
      data-testid="admin-ghl-panel"
      className="mt-6 p-4 rounded-xl border border-zinc-800 bg-zinc-900/30"
    >
      <div className="flex items-center gap-2 mb-3">
        <Plug className="w-4 h-4 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          GoHighLevel
        </div>
      </div>

      {state.status === "loading" && (
        <div className="text-sm text-zinc-500">Loading…</div>
      )}

      {state.status === "not_connected" && (
        <div className="space-y-3">
          <div className="text-sm text-zinc-400">
            Connect this member&apos;s CRM sub-account so their Smart Lead Scoring agent can
            read contacts + conversations and write scores back.
          </div>
          <input
            data-testid="admin-ghl-location-id"
            value={locId}
            onChange={(e) => setLocId(e.target.value)}
            placeholder="Location ID (e.g. ABC1234xyz…)"
            className="w-full text-sm bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
          />
          <input
            data-testid="admin-ghl-pit"
            type="password"
            value={pit}
            onChange={(e) => setPit(e.target.value)}
            placeholder="Private Integration Token (write-only, never shown again)"
            className="w-full text-sm bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500/40"
          />
          <button
            data-testid="admin-ghl-connect-btn"
            onClick={connect}
            disabled={busy}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
            {busy ? "Connecting…" : "Connect"}
          </button>
          <div className="text-[11px] text-zinc-600 leading-relaxed">
            The PIT is encrypted at rest with the app&apos;s <code className="text-zinc-400">GHL_ENC_KEY</code>. We&apos;ll
            verify the credentials with one probe call, then auto-create two custom fields
            on Contact — &quot;AI Lead Score&quot; and &quot;AI Score Reason&quot; — or reuse them if they exist.
          </div>
        </div>
      )}

      {state.status === "connected" && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Location</div>
              <div className="mt-0.5 font-mono text-emerald-300" data-testid="admin-ghl-location-display">
                {state.location_id}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Token</div>
              <div className="mt-0.5 font-mono text-zinc-500">{state.pit_masked || "••••"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Connected</div>
              <div className="mt-0.5 text-zinc-300">
                {state.connected_at ? new Date(state.connected_at).toLocaleString() : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Last verified</div>
              <div className="mt-0.5 text-zinc-300">
                {state.last_verified_at ? new Date(state.last_verified_at).toLocaleString() : "—"}
              </div>
            </div>
            <div className="sm:col-span-2 text-[11px] text-zinc-500">
              Score field:{" "}
              <span className="font-mono text-zinc-400">{state.score_field_id?.slice(0, 12) || "—"}</span>
              <span className="mx-2">·</span>
              Reason field:{" "}
              <span className="font-mono text-zinc-400">{state.why_field_id?.slice(0, 12) || "—"}</span>
              <span className="mx-2">·</span>
              Sending:{" "}
              {state.send_scope ? (
                <span className="text-emerald-400">enabled</span>
              ) : (
                <span className="text-amber-400">missing scope</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              data-testid="admin-ghl-test-btn"
              onClick={test}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-800 text-zinc-300 hover:border-emerald-500/30 hover:text-emerald-300 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Test connection
            </button>
            <button
              data-testid="admin-ghl-disconnect-btn"
              onClick={disconnect}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors disabled:opacity-50"
            >
              <X className="w-3.5 h-3.5" /> Disconnect
            </button>
            <a
              href={`${(state.ui_base_url || "https://app.leadconnectorhq.com").replace(/\/$/, "")}/v2/location/${state.location_id}/settings/custom-fields`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open in CRM
            </a>
          </div>

          {/* Conversation Bridge row — spec B1 admin-side control */}
          <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="admin-bridge-row">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-1 inline-flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> Employz WhatsApp Bridge
                </div>
                <div className="flex items-center gap-2 mb-1.5">
                  {(() => {
                    const bs = bridge?.bridge_state || (bridge?.status === "active" ? "active" : bridge?.status === "reauth_needed" ? "reauth_needed" : "not_installed");
                    const badge = BRIDGE_BADGE[bs] || BRIDGE_BADGE.not_installed;
                    return (
                      <span
                        data-testid={`admin-bridge-badge-${bs}`}
                        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${badge.cls}`}
                      >
                        Bridge: {badge.label}
                      </span>
                    );
                  })()}
                  {bridge?.location_id && (
                    <span className="text-[10px] text-zinc-500 font-mono truncate">loc {bridge.location_id}</span>
                  )}
                </div>
                <div className="text-xs text-zinc-400" data-testid="admin-bridge-reason">
                  {bridge?.is_operational ? (
                    <span>Inbound WhatsApp posts to GHL Conversations. Note+task mirroring is OFF while operational.</span>
                  ) : (
                    <span>{bridge?.reason || "Inbound WhatsApp is mirrored via notes + tasks (legacy)."}</span>
                  )}
                </div>
                {lastAttempt && (
                  <div className="mt-2 text-[10px] text-zinc-500" data-testid="admin-bridge-last-attempt">
                    <span className="uppercase tracking-[0.16em]">Last install attempt:</span>{" "}
                    <span className={lastAttempt.outcome === "success" ? "text-emerald-400" : "text-rose-400"}>
                      {lastAttempt.outcome}
                    </span>{" "}
                    <span className="text-zinc-600">·</span>{" "}
                    <span>{lastAttempt.phase}</span>{" "}
                    {lastAttempt.attempted_at && (
                      <span className="text-zinc-600">· {new Date(lastAttempt.attempted_at).toLocaleString()}</span>
                    )}
                    {lastAttempt.ghl_status_code && (
                      <span className="text-zinc-600"> · GHL {lastAttempt.ghl_status_code}</span>
                    )}
                    {(lastAttempt.error || lastAttempt.ghl_error_body) && (
                      <pre className="mt-1 whitespace-pre-wrap text-[10px] text-rose-300/90 bg-zinc-950 border border-zinc-800 rounded p-2 font-mono max-h-32 overflow-auto">
                        {lastAttempt.ghl_error_body || lastAttempt.error}
                      </pre>
                    )}
                  </div>
                )}
                {bridge?.last_inbound_bridge_diag && (
                  <details className="mt-2 text-[10px] text-zinc-500" data-testid="admin-bridge-last-inbound">
                    <summary className="cursor-pointer hover:text-zinc-300">
                      <span className="uppercase tracking-[0.16em]">Last inbound bridge post:</span>{" "}
                      {bridge.last_inbound_bridge_diag.error ? (
                        <span className="text-rose-400">failed</span>
                      ) : (
                        <span className="text-emerald-400">ok</span>
                      )}
                      {bridge.last_inbound_bridge_diag.status && (
                        <span className="text-zinc-600"> · HTTP {bridge.last_inbound_bridge_diag.status}</span>
                      )}
                      {bridge.last_inbound_bridge_at && (
                        <span className="text-zinc-600"> · {new Date(bridge.last_inbound_bridge_at).toLocaleString()}</span>
                      )}
                    </summary>
                    <div className="mt-1 space-y-1">
                      {bridge.last_inbound_bridge_diag.url && (
                        <div className="text-zinc-600 break-all">POST {bridge.last_inbound_bridge_diag.url}</div>
                      )}
                      {bridge.last_inbound_bridge_diag.request && (
                        <pre className="whitespace-pre-wrap bg-zinc-950 border border-zinc-800 rounded p-2 font-mono max-h-32 overflow-auto text-zinc-400">
{JSON.stringify(bridge.last_inbound_bridge_diag.request, null, 2)}
                        </pre>
                      )}
                      {(bridge.last_inbound_bridge_diag.response_body || bridge.last_inbound_bridge_diag.error) && (
                        <pre className={`whitespace-pre-wrap bg-zinc-950 border border-zinc-800 rounded p-2 font-mono max-h-32 overflow-auto ${bridge.last_inbound_bridge_diag.error ? "text-rose-300/90" : "text-emerald-300/90"}`}>
                          {bridge.last_inbound_bridge_diag.error || bridge.last_inbound_bridge_diag.response_body}
                        </pre>
                      )}
                    </div>
                  </details>
                )}
                {!bridge?.provider_configured && bridge?.bridge_state !== "not_installed" && (
                  <div className="mt-2 text-[10px] text-amber-400 inline-flex items-center gap-1" data-testid="admin-bridge-provider-warning">
                    <AlertTriangle className="w-3 h-3" /> GHL_CONV_PROVIDER_ID is not set on the server — bridge will fall back to notes even with a valid token.
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {bridge?.is_operational ? (
                  <button onClick={disconnectBridge} disabled={bridgeBusy} data-testid="admin-bridge-disconnect" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-3 py-2 border border-rose-500/30 text-rose-300 rounded">
                    {bridgeBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />} Disconnect bridge
                  </button>
                ) : (
                  <button onClick={startBridgeInstall} disabled={bridgeBusy} data-testid="admin-bridge-install" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-3 py-2 border border-emerald-500/40 text-emerald-300 rounded">
                    {bridgeBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />} {bridge?.bridge_state === "not_installed" ? "Install bridge" : "Reinstall bridge"}
                  </button>
                )}
              </div>
            </div>
            {bridge?.is_operational && bridge?.expires_at && (
              <div className="mt-2 text-[10px] text-zinc-600">
                Auto-refreshes ~{new Date(bridge.expires_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
